import {
  DAY_FORMAT,
  Timeframe,
  TIMEKEY_FORMAT
} from '@dadohr/shared/domainTypes/time';
import firebase from 'firebase/app';
import { compact } from 'lodash';
import padStart from 'lodash/padStart';
import moment from 'moment-timezone';
import { UnsafeTFunction } from '../../i18next';

const DEFAULT_TZ = 'UTC';

export const ONE_MINUTE = 1 * 1000 * 60;
export const FIFTY_MINUTES = ONE_MINUTE * 50;
export const ONE_DAY = ONE_MINUTE * 60 * 24;

export const SIMPLE_DATE = 'MMM D';
export const PRECISE_DATE = 'YYYY/MM/DD HH:mm';
export const HUMAN_DATE = 'MMM D, YYYY (HH:mm)';
export const HUMAN_DATE_SHORT = 'MMM D, YYYY';

export const guessCurrentTimezone = () => {
  return moment.tz.guess() || DEFAULT_TZ;
};

export const getTzAbbr = (timezone: string) => {
  const zone = moment.tz.zone(timezone);
  return zone ? zone.abbr(Date.now()) : timezone;
};

export const formatDate = (
  d: moment.Moment,
  format = 'MMMM Do YYYY, h:mm:ss a'
): string => {
  return d.format(format);
};

export const isTimestamp = (d: any): d is Timestamp => {
  return d instanceof firebase.firestore.Timestamp;
};

export const formatSmartRelativeDate = (
  d: moment.Moment | number | Date | string | firebase.firestore.Timestamp,
  format: string = HUMAN_DATE
): string => {
  const momentNow = moment(new Date());
  const momentDate = isTimestamp(d) ? toMoment(d) : moment(d);
  const diff = momentNow.diff(momentDate, 'days');

  if (diff < 30) {
    return momentDate.fromNow();
  }
  return momentDate.format(format);
};

export type DateLike =
  | moment.Moment
  | number
  | Date
  | string
  | firebase.firestore.Timestamp;

export const formatDatePrecise = (
  d: DateLike,
  format = PRECISE_DATE
): string => {
  if (isTimestamp(d)) {
    return formatDate(toMoment(d), format);
  }
  return formatDate(moment(d), format);
};

export const formatDateHuman = (
  d: DateLike,
  short: boolean = false
): string => {
  const f = short ? HUMAN_DATE_SHORT : HUMAN_DATE;
  if (isTimestamp(d)) {
    return formatDate(toMoment(d), f);
  }
  return formatDate(moment(d), f);
};

export const formatDateRange = (
  start: DateLike,
  end: DateLike,
  format = HUMAN_DATE
) => `${formatDatePrecise(start, format)} - ${formatDatePrecise(end, format)}`;

export const toMoment = (d: firebase.firestore.Timestamp) => {
  return moment(d.toMillis());
};

export const formatPossiblyYearlessMoment = (
  d: moment.Moment,
  dateFormat: string,
  forceYearless: boolean = false
) => {
  const momentToFormat = forceYearless ? d.set('year', 1900) : d;
  const yearStringRegex = /(-|\/|\.|(,\s+))?1900(-|\/|\.)?/g;
  const formattedMoment = momentToFormat
    .format(dateFormat)
    .replace(yearStringRegex, '');

  return formattedMoment;
};

export type SerializedTimestamp = { _nanoseconds: number; _seconds: number };

/** Firestore Timestamp-like plain object (e.g. from JSON or RPC). */
export type PlainTimestampLike = {
  seconds: number;
  nanoseconds: number;
};

const isSerializedTimestamp = (t: any): t is SerializedTimestamp => {
  const isObject = t instanceof Object;
  const hasNanoseconds = t.hasOwnProperty('_nanoseconds');
  const hasSeconds = t.hasOwnProperty('_seconds');
  return isObject && hasNanoseconds && hasSeconds;
};

const isPlainTimestampLike = (t: any): t is PlainTimestampLike => {
  const isObject = t instanceof Object;
  const hasSeconds = typeof t?.seconds === 'number';
  const hasNanoseconds = typeof t?.nanoseconds === 'number';
  return isObject && hasSeconds && hasNanoseconds;
};

/**
 * Returns milliseconds for Firestore Timestamp or plain {seconds, nanoseconds} / {_seconds, _nanoseconds}.
 * Use when snapshot data may contain plain objects instead of Timestamp instances.
 * Returns undefined if value is not timestamp-like (caller can treat as non-date field).
 */
export const timestampToMillisSafe = (val: unknown): number | undefined => {
  if (val == null) return undefined;
  if (isTimestamp(val)) return (val as firebase.firestore.Timestamp).toMillis();
  if (isPlainTimestampLike(val)) return val.seconds * 1000 + val.nanoseconds / 1e6;
  if (isSerializedTimestamp(val)) return val._seconds * 1000 + val._nanoseconds / 1e6;
  return undefined;
};

export const toMomentFromAny = (
  d:
    | Timestamp
    | string
    | number
    | SerializedTimestamp
    | PlainTimestampLike
) => {
  if (isTimestamp(d)) {
    return moment(d.toMillis());
  }
  if (isSerializedTimestamp(d)) {
    return moment(d._seconds * 1000);
  }
  if (isPlainTimestampLike(d)) {
    return moment(d.seconds * 1000 + d.nanoseconds / 1e6);
  }

  return moment(d);
};

export const fromMoment = (d: moment.Moment) => {
  return firebase.firestore.Timestamp.fromMillis(d.valueOf());
};

export const timeBetween = (
  start: firebase.firestore.Timestamp,
  end: firebase.firestore.Timestamp
) => {
  const startMoment = toMoment(start);
  const endMoment = toMoment(end);

  return endMoment.diff(startMoment, 'days');
};

export const now = (): firebase.firestore.Timestamp =>
  // firestore is not defined in tests - provide a mock Timestamp instead.
  firebase.firestore
    ? firebase.firestore.Timestamp.now()
    : {
        seconds: 0,
        nanoseconds: 0,
        toDate: () => new Date(),
        toMillis: () => 0,
        isEqual: (other) => other.toMillis() === 0,
        valueOf: () => ''
      };

export const nowRdb = () => {
  const ts = now();
  return { _seconds: ts.seconds, _nanoseconds: ts.nanoseconds };
};
export const tsFromRdb = (
  ts: { _seconds: number; _nanoseconds: number } | null
) => {
  if (!ts) {
    return null;
  }
  return new firebase.firestore.Timestamp(ts._seconds, ts._nanoseconds);
};

export const tsFromMillis = (ms: number) => {
  return firebase.firestore.Timestamp.fromMillis(ms);
};

export const nullableTsToIso = (ts: firebase.firestore.Timestamp | null) => {
  return ts?.toDate().toISOString() ?? null;
};

export const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

export const weekdayToNumber = (weekday: string): number =>
  WEEKDAYS.indexOf(weekday);
export const numberToWeekday = (weekday: number): string => WEEKDAYS[weekday];

export const humanizeDuration = (ms: number, withSuffix = false) =>
  moment.duration(ms, 'ms').humanize(withSuffix);

export const weeksAndDays = (durationInDays: number) => {
  const weeks = Math.floor(durationInDays / 7);
  const days = durationInDays % 7;
  return { weeks, days };
};

export const getTimeElapsed = (start: moment.Moment, end: moment.Moment) => {
  const seconds = moment.duration(end.diff(start)).asSeconds();

  const minutes = Math.floor(seconds / 60).toFixed(0);
  const remainingSeconds = Math.round(seconds % 60).toFixed(0);

  return `${padStart(minutes, 2, '0')}:${padStart(remainingSeconds, 2, '0')}`;
};

export const compareTimestamps = (
  a: firebase.firestore.Timestamp,
  b: firebase.firestore.Timestamp
) => {
  const aMs = a.toMillis();
  const bMs = b.toMillis();
  if (aMs === bMs) {
    return 0;
  }
  return aMs < bMs ? -1 : 1;
};

export const isBefore = (
  a: firebase.firestore.Timestamp,
  b: firebase.firestore.Timestamp
) => {
  return a.toMillis() < b.toMillis();
};

export type Timestamp = firebase.firestore.Timestamp;
export type MomentRange = { start: moment.Moment; end: moment.Moment };
export type TimestampRange = { start: Timestamp; end: Timestamp };

// Timeframe's are an exclusive timerange!
export const timeframeToMomentRange = (tf: Timeframe): MomentRange => ({
  start: moment.tz(tf.start, DAY_FORMAT, tf.tz).startOf('d'),
  end: moment.tz(tf.end, DAY_FORMAT, tf.tz).subtract(1, 'd').endOf('d')
});

export const timeframeToTimestampRange = (tf: Timeframe): TimestampRange => {
  const r = timeframeToMomentRange(tf);
  return { start: fromMoment(r.start), end: fromMoment(r.end) };
};

export const msToSecs = (ms: number, digits = 4) => {
  const s = Math.floor(ms / 1000);
  const restMs = Math.round(ms % 1000);
  console.log(ms, s, restMs);
  return `${parseFloat(`${s}.${padStart(`${restMs}`, 4, '0')}`).toFixed(
    digits
  )}s`;
};

export const msToMinsAndSecs = (ms: number) => {
  const seconds = Math.round(ms / 1000);
  const minutes = Math.floor(seconds / 60).toFixed(0);
  const remainingSeconds = Math.round(seconds % 60).toFixed(0);

  return `${padStart(minutes, 2, '0')}:${padStart(remainingSeconds, 2, '0')}`;
};

export const toCappedTimekey = (
  ts: firebase.firestore.Timestamp,
  tz: string
) => {
  const m = toMoment(ts).tz(tz);
  if (m.hour() < 12) {
    m.subtract(1, 'd');
  }
  return m.format(TIMEKEY_FORMAT);
};

// always inclusive on both sides
export const isBetweenMs = (x: number, start: number, end: number) => {
  return start <= x && x <= end;
};

export const isBetween = (
  x: moment.Moment,
  start: moment.Moment,
  end: moment.Moment
) => {
  return start.isSameOrBefore(x) && x.isSameOrBefore(end);
};

export const areOverlappingRanges = <T extends MomentRange>(a: T, b: T) => {
  if (a.start.isSame(b.start)) {
    return true;
  }
  if (a.start.isBefore(b.start) && a.end.isAfter(b.start)) {
    return true;
  }
  if (a.end.isSame(b.end)) {
    return true;
  }
  if (a.end.isAfter(b.end) && a.start.isBefore(b.end)) {
    return true;
  }
  if (isBetween(a.start, b.start, b.end)) {
    // with all other checks made before, we also know that
    // isBetween(a.end, b.start, b.end) === true;
    return true;
  }
  return false;
};

export const toEndOfPreviousDay = (m: moment.Moment) =>
  m.clone().subtract(1, 'd').endOf('d');
export const toStartOfNextDay = (m: moment.Moment) =>
  m.clone().add(1, 'd').startOf('d');

const TZ_CACHE: {
  [tz: string]: {
    [ms: string]: moment.Moment;
  };
} = {};

export const getMomentInTz = (tz: string, ms: number) => {
  const cache = (TZ_CACHE[tz] = TZ_CACHE[tz] || {});
  return (cache[ms] = cache[ms] || moment(ms).tz(tz)).clone();
};

export const tsIsAfter = (after: Timestamp, before: Timestamp) => {
  return after.toMillis() > before.toMillis();
};

export const addToTs = (ts: Timestamp, ms: number) => {
  return tsFromMillis(ts.toMillis() + ms);
};

const HOUR_WITH_TZ_FORMAT = 'HH:mm z';
export const printHourWithTimezone = (hour: number, tz: string) => {
  return moment.tz(tz).hour(hour).startOf('h').format(HOUR_WITH_TZ_FORMAT);
};

// This needs to receive a full timestamp object, otherwise it's a bit brittle,
// because it cannot take daylight saving times into account!
export const printTz = (tz: string) => {
  return moment.tz(tz).format('z');
};

export const toWeekOrDayDuration = (
  ms: number,
  t: UnsafeTFunction,
  opts: { forceSingular?: boolean } = {}
) => {
  const days = Math.abs(Math.round(ms / ONE_DAY));
  if (days % 7 === 0) {
    const weeks = days / 7;
    if (opts.forceSingular) {
      return `${weeks} ${t('week')}`;
    }
    return weeks === 1 ? `1 ${t('week')}` : `${weeks} ${t('weeks')}`;
  }
  if (opts.forceSingular) {
    return `${days} ${t('day')}`;
  }
  return days === 1 ? `1 ${t('day')}` : `${days} ${t('days')}`;
};

export const toWeekAndDayDuration = (ms: number, t: UnsafeTFunction) => {
  const { weeks, days } = weeksAndDays(Math.round(ms / ONE_DAY));
  const remainingDays = days % 7;
  return compact([
    weeks ? `${weeks} ${weeks === 1 ? t('week') : t('weeks')}` : null,
    remainingDays
      ? `${remainingDays} ${remainingDays === 1 ? t('day') : t('days')}`
      : null
  ]).join(', ');
};

export const fromIsoString = (d: string) => {
  return firebase.firestore.Timestamp.fromDate(new Date(d));
};

export const cleanOutEpochDate = (d: Timestamp | null) => {
  return d ? ((d.toMillis() || 0) <= ONE_DAY ? null : d) : null; // ONE_DAY and not 0, because of timezoning...
};
